import React, { useCallback, useMemo, useState } from 'react';

import cn from 'classnames';

import Dropdown from 'components/dropdown';
import Typography from 'components/typography';
import { Blockchain, Date, RowNumber } from 'containers/row-renderers';

import {
  CompanyColumnType,
  GetTableDataArgsType,
  PossibleTableItemsType,
  RendererProps,
  ValueRowType,
} from 'types/tableTypes';
import { SCORING_STATUS_ENUM } from 'types/transactionTypes';

import { ReactComponent as ArrowDown } from 'assets/icons/arrow-down-icon.svg';
import { ReactComponent as CheckIconInverted } from 'assets/icons/check-outlined-icon.svg';
import { ReactComponent as CrossIcon } from 'assets/icons/cross-icon.svg';

import useQueryParamsHook from 'utils/useQueryParams.hook';

import { MODAL_TYPE } from '../scoring-request-modal/scoringRequestModal';
import s from './ScoringRequestTableView.module.scss';

const DROPDOWN_ITEMS = [
  {
    label: (
      <Typography className={cn(s.option, s.approvedOption)}>
        <CheckIconInverted className={s.checkIcon} /> Approve
      </Typography>
    ),
    key: MODAL_TYPE.APPROVE_KEY,
  },
  {
    label: (
      <Typography className={cn(s.option, s.declinedOption)}>
        <CrossIcon className={s.crossIcon} /> Decline
      </Typography>
    ),
    key: MODAL_TYPE.DECLINE_KEY,
  },
];

interface UseTableColumnsProps {
  openCloseModalHandler: (key: string, id: string) => void;
}

const useTableColumns = (props: UseTableColumnsProps): CompanyColumnType[] => {
  const { openCloseModalHandler } = props;

  useQueryParamsHook();

  const [isOverlayOpened, setIsOverlayOpened] = useState<Record<string, boolean>>({});
  const handleOpenOverlay = (id: string) => {
    setIsOverlayOpened((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const handleItemClick = useCallback(
    (key: string, id: string) => {
      openCloseModalHandler(key, id);
      handleOpenOverlay(id);
    },
    [openCloseModalHandler],
  );

  const withdrawalRequest = useCallback(
    (
      status: ValueRowType,
      record: RendererProps<GetTableDataArgsType, unknown, PossibleTableItemsType>,
    ) => {
      const typedStatus: SCORING_STATUS_ENUM = status as unknown as SCORING_STATUS_ENUM;
      const id = record?.elementData?.id as string;
      const isOpened = isOverlayOpened[id];

      if (typeof typedStatus !== 'number') {
        return <></>;
      }

      if (typedStatus === SCORING_STATUS_ENUM.UNREVIEWED) {
        return (
          <Dropdown
            align={{ offset: [0, 4] }}
            open={isOpened}
            onOpenChange={() => handleOpenOverlay(id)}
            handleItemClick={(event: { key: string }) => handleItemClick(event.key, id)}
            trigger={['click']}
            items={DROPDOWN_ITEMS}>
            <button className={cn(s.dropdownButton, { [s.active]: isOpened })}>
              <Typography>New request</Typography> <ArrowDown />
            </button>
          </Dropdown>
        );
      }

      const label = typedStatus === SCORING_STATUS_ENUM.APPROVED ? 'Approved' : 'Declined';

      return (
        <div
          className={cn(
            s.inactiveStatus,
            typedStatus === SCORING_STATUS_ENUM.APPROVED ? s.approvedStatus : s.declinedStatus,
          )}>
          {label}
        </div>
      );
    },
    [handleItemClick, isOverlayOpened],
  );

  return useMemo(
    () => [
      { title: '#', key: 'number', renderer: RowNumber, className: s.number },
      {
        title: 'Request ID',
        key: 'id',
        renderer: (value: ValueRowType) => {
          if (typeof value !== 'string') return <>—</>;
          return <span className={s.requestId}>{value.slice(0, 8)}…</span>;
        },
      },
      {
        title: 'Request date',
        key: 'created_date',
        renderer: Date,
        sortable: true,
      },
      {
        title: 'Blockchain',
        key: 'blockchain',
        renderer: (blockchain: ValueRowType) => Blockchain(blockchain),
        sortable: true,
      },
      {
        title: 'Fiat value',
        key: 'amount',
        renderer: (amountInFiat: ValueRowType) => {
          if (typeof amountInFiat !== 'number') {
            return <>—</>;
          }
          return (
            <>
              {amountInFiat.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              $
            </>
          );
        },
        sortable: true,
      },
      {
        title: 'Risk score',
        key: 'risk_score',
        renderer: (riskScore: ValueRowType) => {
          if (typeof riskScore === 'number') {
            let style = 'red';
            if (riskScore <= 20) {
              style = 'green';
            } else if (riskScore > 20 && 60 > riskScore) {
              style = 'yellow';
            }

            return <span className={s[style]}>{riskScore}</span>;
          }

          return <>—</>;
        },
        sortable: true,
      },
      {
        title: 'Action',
        key: 'status',
        renderer: withdrawalRequest,
      },
    ],
    [withdrawalRequest],
  );
};

export default useTableColumns;
